import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Editor as TinyMCEEditor, EditorOptions, EditorEvent } from 'tinymce';
import { Link } from '@setvi/shared/interfaces';
import {
  getPreviewLink,
  normalizeLinkToPreviewLink
} from '@setvi/shared/utils';
import { generateLinkViewerUrl } from '@setvi/shared/utils/viewer';
import { EditorActions, KeyCode, QUOTE_SHARE_LINK_CLASS } from '../../enums';

import {
  IEditorLink,
  SEditorType,
  IEditorHook,
  IHandleLinkOptions,
  PastePostProcessEvent,
  QuoteShareLinkTarget
} from '../../interfaces';
import { viewIcon, editIcon, removeIcon } from '../../components';
import { selectedProductLinkClassName } from '../use-editor-products';

export const useEditorLinksHook = ({
  editorRef,
  importedLinks,
  getLinkAction,
  getLinks
}: IEditorHook) => {
  const [links, setLinks] = useState<IEditorLink[]>([]);
  const [currentId, setCurrentId] = useState(null);

  const [clean, setClean] = useState(false);

  // We have a lot links with old logic so this is backup to recognize old links and replace them with new ones
  useEffect(() => {
    if (editorRef && importedLinks?.length) {
      const content = editorRef?.getBody();
      const oldLinks = content?.querySelectorAll('a[href].immutableLink');

      if (oldLinks?.length) {
        oldLinks.forEach((aTag: HTMLElement) => {
          const currentLink = importedLinks?.find(
            elem =>
              elem?.Placeholder === aTag.dataset.id ||
              elem?.Placeholder === aTag.getAttribute('href')
          );

          const normalizedLink = normalizeLinkToPreviewLink(currentLink);
          const href = generateLinkViewerUrl(normalizedLink);

          const placeholder = currentLink?.Placeholder;

          aTag.setAttribute('id', placeholder); // Change 'newIdValue' to the new id value
          aTag.setAttribute('href', href); // Change 'newHrefValue' to the new href value
          aTag.classList.remove('immutableLink'); // Add a new class or modify existing classes
        });
      }

      const bodyLinks = content?.querySelectorAll('a[href][id]');
      const arrayFromNodeList = Array.from(bodyLinks);

      // Deduplicate importedLinks first to prevent duplicates from propagating
      const uniqueImportedLinks = importedLinks.filter(
        (link, index, self) =>
          index === self.findIndex(l => l.Placeholder === link.Placeholder)
      );

      const filteredLinks = uniqueImportedLinks
        .filter(link => !links?.find(e => e.Placeholder === link.Placeholder))
        .map(link => ({
          ...link,
          vissible: true
        }));

      filteredLinks?.forEach(async link => {
        if (arrayFromNodeList.find(e => e.id === link.Placeholder)) return;

        const previewHref = await generateLinkViewerUrl(getPreviewLink(link));

        await editorRef.execCommand(
          'mceInsertContent',
          false,
          `<a id=${link?.Placeholder} href=${link?.Placeholder} target="_blank" rel="noreferrer noopener" data-preview-href=${previewHref}>${link?.Name}</a> `
        );
      });

      setLinks(prev => {
        const filtered = prev.filter(
          link => !filteredLinks.find(e => e.Placeholder === link.Placeholder)
        );

        return [...filtered, ...filteredLinks];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRef, importedLinks]);

  useEffect(() => {
    if (currentId) {
      setLinks(prev =>
        prev.map(link => ({
          ...link,
          vissible: currentId === link.Placeholder ? false : link?.vissible
        }))
      );
      getLinks?.(links.filter(link => currentId !== link.Placeholder));
      setCurrentId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  useEffect(() => {
    if (!editorRef || !clean) return;

    const aTagsToRemove = editorRef
      ?.getBody()
      ?.querySelectorAll('a:only-child > br[data-mce-bogus="1"]');

    const hideLinksId: string[] = [];

    // Remove each found <a> tag
    aTagsToRemove.forEach(brElement => {
      const aTag = brElement.parentNode; // Get the parent <a> element
      if ((aTag as Element)?.id) hideLinksId.push((aTag as Element).id);
      aTag.parentNode.removeChild(aTag); // Remove the <a> element
    });

    setLinks(prev =>
      prev.map(link => ({
        ...link,
        vissible: hideLinksId.includes(link?.Placeholder)
          ? false
          : link?.vissible
      }))
    );

    setClean(false);
  }, [editorRef, clean]);

  /**
   * The anchors the quote share flow put in the email, in the order they
   * appear. An email can carry several — one per quote shared into it.
   *
   * A quote share link is deliberately NOT a `Link`: there is no resource
   * record behind it, and it must stay out of the `links` array, which builds
   * the send payload. They are recognised by their class alone (DEV-785).
   */
  const getQuoteShareNodes = (editorNode?: TinyMCEEditor) =>
    Array.from(
      (editorRef || editorNode)
        ?.getBody()
        ?.querySelectorAll<HTMLElement>(`a.${QUOTE_SHARE_LINK_CLASS}`) || []
    );

  /**
   * The one a caller means. Addressed by position, since two links can point
   * at the same share, and verified by href so an edit still lands on the
   * right anchor if the email changed after the caller read it.
   */
  const resolveQuoteShareNode = (target?: QuoteShareLinkTarget) => {
    const nodes = getQuoteShareNodes();
    if (!nodes.length) return null;
    if (!target) return nodes[0];

    const atIndex = nodes[target.index];
    if (
      atIndex &&
      (!target.href || atIndex.getAttribute('href') === target.href)
    )
      return atIndex;

    return (
      nodes.find(node => node.getAttribute('href') === target.href) || null
    );
  };

  /**
   * Rename a quote share link, i.e. change the text the recipient clicks.
   *
   * A resource link is renamed through the Review Links drawer, which writes
   * back through the `Link` record behind it. A quote share link has no such
   * record, so renaming it means editing the text in the email itself — from
   * the link's own toolbar, or from the drawer's quote row.
   *
   * The href is left alone, so the pre-send swap from the preview URL to the
   * tracking URL still finds its link.
   */
  const renameQuoteShareLink = (
    name: string,
    quoteLink?: QuoteShareLinkTarget
  ) => {
    const editor = editorRef;
    const target = resolveQuoteShareNode(quoteLink);
    const nextName = name?.trim();

    // An empty name would leave an anchor with no text to click, which the
    // editor's own cleanup then strips — keep the current one instead.
    if (!editor || !target || !nextName) return;

    // Set through the editor's DOM API so the name goes in as text, inside a
    // transaction so the whole rename is a single undo level (a direct DOM
    // write is invisible to the undo manager on its own).
    editor.undoManager.transact(() => {
      editor.dom.setHTML(target, editor.dom.encode(nextName));
    });
  };

  /** Take one quote share link out of the email. */
  const removeQuoteShareLink = (quoteLink?: QuoteShareLinkTarget) => {
    const editor = editorRef;
    const target = resolveQuoteShareNode(quoteLink);

    if (!editor || !target) return;

    editor.undoManager.transact(() => {
      editor.dom.remove(target);
    });
  };

  const editLink = (editorNode: TinyMCEEditor) => {
    const editor = editorRef || editorNode;
    const selection = editor?.selection;
    const linkNode = selection?.getNode();

    const Id = linkNode?.id || linkNode?.getAttribute('href');

    const quoteShareIndex =
      linkNode?.nodeName === 'A' &&
      linkNode.classList.contains(QUOTE_SHARE_LINK_CLASS)
        ? getQuoteShareNodes(editorNode).indexOf(linkNode as HTMLElement)
        : -1;

    // Both kinds of link are renamed in the same drawer — a resource link by
    // the `Link` record `id` resolves to, a quote share link by the row at the
    // position below (DEV-785).
    getLinkAction?.({
      action: EditorActions.EDIT,
      id: Id,
      quoteShareLinkIndex: quoteShareIndex < 0 ? undefined : quoteShareIndex
    });
  };

  const deleteLink = (
    editorNode: TinyMCEEditor,
    preventDefault?: () => void
  ) => {
    const editor = editorRef || editorNode;
    const selection = editor?.selection;
    const selectedNode = selection?.getNode();

    const highlitedContent = selection?.getContent({ format: 'html' });

    if (highlitedContent) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = highlitedContent;
      const aElements = tempDiv.querySelectorAll('a[href]');

      const deletedLinks: string[] = [];
      aElements.forEach(a => {
        deletedLinks.push(a?.id);
      });

      setLinks(prev =>
        prev.map(link => ({
          ...link,
          vissible: deletedLinks.includes(link?.Placeholder)
            ? false
            : link?.vissible
        }))
      );
    }

    if (selectedNode?.tagName === 'A') {
      // If the selected node is an <a> tag, delete only the selected node
      const range = selection.getRng();
      range.setStartBefore(selectedNode);
      range.setEndAfter(selectedNode);
      selection.setRng(range);

      setCurrentId(selectedNode?.id);

      editor.execCommand('Delete');
      setLinks(prev =>
        prev.map(link => ({
          ...link,
          vissible:
            link?.Placeholder === selectedNode?.id ? false : link?.vissible
        }))
      );

      preventDefault?.();
    }
  };

  const viewLink = (editorNode: TinyMCEEditor) => {
    const selection = editorRef?.selection || editorNode?.selection;

    const linkNode = selection?.getNode();

    const link =
      linkNode?.getAttribute('data-preview-href') ||
      linkNode?.getAttribute('href');

    window.open(link, '_blank').focus();
  };

  const handleCursorIsInsideLink = (editor: SEditorType) => {
    const bookmark = editor.selection.getBookmark();
    editor.selection.moveToBookmark(bookmark);

    // Check if the cursor is inside a link
    const node = editor.selection.getNode();

    if (node.nodeName === 'A' && node?.hasAttribute('id')) {
      const range = editor.selection.getRng();
      // Check if cursor is at the beginning  of the link
      const start = range.startOffset === 1 && range.endOffset === 1;
      if (start) {
        // Move the cursor to the beginning  of the link
        const newText = editor.getDoc().createTextNode(' ');
        node.parentNode.insertBefore(newText, node);
        range.setStartBefore(newText);
        range.setEndBefore(node);
        editor.selection.setRng(range);
      } else {
        // Move the cursor to the  ending of the link
        range.setEndAfter(node);
        range.collapse(false);
        editor.selection.setRng(range);
        editor.execCommand('mceInsertContent', false, '');
      }
    }
  };

  const importPastedLinks = (
    e: EditorEvent<PastePostProcessEvent>,
    editor: TinyMCEEditor
  ) => {
    const pastedLinksNodes = e?.node?.querySelectorAll(
      'a[href][id][data-preview-href]'
    );
    const pastedLinks = Array.from(pastedLinksNodes);

    if (!pastedLinks?.length) return;
    e.preventDefault();

    const paragraph = e?.node?.querySelectorAll('p');

    setLinks(prev => {
      const newLinks: IEditorLink[] = [];

      if (paragraph?.length) {
        paragraph.forEach(p => {
          const paragraphLinks = p?.querySelectorAll('a[href][id]');
          const paragraphLinksArray = Array.from(paragraphLinks);

          paragraphLinksArray.forEach(link => {
            const findLink = prev.findIndex(l => l.Placeholder === link?.id);

            const placeholder = uuidv4();
            link.setAttribute('id', placeholder);
            link.setAttribute('href', placeholder);

            newLinks.push({
              ...prev[findLink],
              vissible: true,
              Placeholder: placeholder
            });
          });
          editor?.execCommand('mceInsertContent', false, p?.innerHTML);
          editor?.execCommand('mceInsertNewLine');
        });
      } else {
        pastedLinks.forEach(link => {
          const findLink = prev.findIndex(l => l.Placeholder === link?.id);

          const placeholder = uuidv4();
          link.setAttribute('id', placeholder);
          link.setAttribute('href', placeholder);

          newLinks.push({
            ...prev[findLink],
            vissible: true,
            Placeholder: placeholder
          });
        });

        editor?.execCommand('mceInsertContent', false, e?.node?.innerHTML);
        editor?.execCommand('mceInsertNewLine');
      }

      const allLinks = [...prev, ...newLinks];
      // filter duplicates
      return allLinks.filter(
        (link, index, self) =>
          index === self.findIndex(l => l.Placeholder === link.Placeholder)
      );
    });
  };

  const verifyLinks = (editor: TinyMCEEditor) => {
    const editorLinks = editor?.getBody().querySelectorAll('a[href][id]');
    const existingLinks = Array.from(editorLinks);
    const special = '&#xFEFF;';

    const emptyLinksArray = existingLinks.filter(
      link => !link?.innerHTML?.trim() || link?.innerHTML === special
    );
    const fullLinksArray = existingLinks.filter(link =>
      link?.innerHTML?.trim()
    );

    if (emptyLinksArray?.length) {
      emptyLinksArray.forEach(link => {
        link.remove();
      });
    }

    setLinks(prev =>
      prev?.map(l => ({
        ...l,
        vissible: emptyLinksArray?.find(e => e?.id === l?.Placeholder)
          ? false
          : !!fullLinksArray?.find(e => e?.id === l?.Placeholder)
      }))
    );

    editorRef?.execCommand('InsertNewBlockAfter');
    editorRef?.execCommand('mceCleanup');
  };

  const handleXYZ = (editorNode: TinyMCEEditor) => {
    const editorLinks = editorNode?.getBody().querySelectorAll('a[href][id]');
    const existingLinks = Array.from(editorLinks);

    // check if cursor is inside a tag
    handleCursorIsInsideLink(editorNode);

    setLinks(prev =>
      prev.map(link => ({
        ...link,
        vissible: !!existingLinks?.find(e => e?.id === link?.Placeholder)
      }))
    );

    editorNode?.execCommand('mceInsertContent', false, '');
    setClean(true);
    verifyLinks(editorNode);
  };

  // Remove whole link for backspace and delete key
  const handleKeyDown = async (
    e: {
      keyCode: number;
      ctrlKey: boolean;
      metaKey: boolean;
      preventDefault: () => void;
    },
    editorNode: TinyMCEEditor
  ) => {
    if (e.keyCode === KeyCode.BACKSPACE || e.keyCode === KeyCode.DELETE) {
      // Quote share links are editable text — let TinyMCE delete character by
      // character instead of removing the whole anchor.
      const node = editorNode?.selection?.getNode();
      if (
        node?.nodeName === 'A' &&
        node.classList.contains(QUOTE_SHARE_LINK_CLASS)
      )
        return;
      await deleteLink(editorNode, e.preventDefault);
    } else {
      // Cmd/Ctrl+Z and Cmd/Ctrl+Y fall through here and return early below, so
      // TinyMCE's native undo/redo runs unobstructed — link visibility is
      // re-synced afterwards via the 'Undo Redo' editor events. Running the
      // content-mutating cleanup synchronously would clobber the native undo
      // level and prevent a deleted link from being restored.
      const ignoredKeyCodes = [37, 38, 39, 40]; //   Arrow keys
      if (e.ctrlKey || e.metaKey || ignoredKeyCodes.includes(e.keyCode)) return;
      await handleCursorIsInsideLink(editorNode);
    }
  };

  const insertLink = (link: Link) => {
    if (!link || !editorRef) return;

    const previewHref = generateLinkViewerUrl(getPreviewLink(link));

    const selection = editorRef?.selection;
    const selectedNode = selection?.getNode();

    // One clean undo level per inserted link.
    editorRef.undoManager.transact(() => {
      if (selectedNode?.tagName === 'A') handleCursorIsInsideLink(editorRef);

      editorRef.execCommand(
        'mceInsertContent',
        false,
        `<a id=${link?.Placeholder} href=${link?.Placeholder} target="_blank" rel="noreferrer noopener" data-preview-href=${previewHref}>${link?.Name}</a>`
      );
    });

    // Filter out any existing link with same Placeholder, then add the new one
    setLinks(prev => [
      ...prev.filter(l => l.Placeholder !== link.Placeholder),
      { ...link, vissible: true }
    ]);
    if (getLinks) getLinks([...links, link]);
  };

  const updateLink = (link: Link, oldLink: Link) => {
    const node = editorRef?.dom
      .getRoot()
      .querySelector(`[id="${oldLink.Placeholder}"]`);

    if (!link || !editorRef || !node) return;

    const href = generateLinkViewerUrl(getPreviewLink(link));

    // Direct DOM writes aren't seen by the undo manager on their own — wrap
    // them in a transaction so the whole edit is exactly one undo level.
    editorRef.undoManager.transact(() => {
      node.textContent = link?.Name;
      node.setAttribute('href', link?.Placeholder);
      node.setAttribute('data-preview-href', href);
      node.setAttribute('id', link?.Placeholder);
    });

    const updatedLinks = links.map(prevLink =>
      prevLink?.Placeholder === oldLink?.Placeholder ? link : prevLink
    );
    setLinks(updatedLinks);

    // We need to regonize if link changed name or items length because of different actions in drawer (one needs to close current item other doesn't)
    getLinkAction?.({
      action:
        link?.Item?.Items?.length === oldLink?.Item?.Items?.length
          ? EditorActions.UPDATE
          : EditorActions.EDIT,
      id: link?.Placeholder
    });

    getLinks?.(updatedLinks);
  };

  const removeLink = (removedLink: Link, editorNode: TinyMCEEditor) => {
    const editor = editorRef || editorNode;
    const body = editor?.getBody();
    const bodyLinks = body?.querySelectorAll('a[href][id]');

    const node = Array.from(bodyLinks)?.find(
      link => link?.id === removedLink?.Placeholder
    );

    if (!node) return;

    setLinks(prev =>
      prev.map(link => ({
        ...link,
        vissible:
          link?.Placeholder === removedLink?.Placeholder
            ? false
            : link?.vissible
      }))
    );

    // Wrap the direct DOM removal so it becomes a single tracked undo level.
    editor.undoManager.transact(() => {
      node.remove();
    });
  };

  const handleSnippet = (value: string) => {
    if (!editorRef) return;

    editorRef?.execCommand('mceInsertContent', false, value);
  };

  const handleLinks = (
    action: EditorActions,
    { link, oldLink, editorNode }: IHandleLinkOptions
  ) => {
    switch (action) {
      case EditorActions.EDIT:
        editLink(editorNode);
        break;
      case EditorActions.DELETE:
        deleteLink(editorNode);
        break;
      case EditorActions.REMOVE:
        removeLink(link, editorNode);
        break;
      case EditorActions.INSERT:
        insertLink(link);
        break;
      case EditorActions.UPDATE:
        updateLink(link, oldLink);
        break;
      default:
        viewLink(editorNode);
    }
  };

  const initLinkOptions: Partial<Omit<EditorOptions, 'selector' | 'target'>> = {
    setup: (editor: TinyMCEEditor) => {
      editor.on('keydown', e => {
        handleKeyDown(e, editor);
      });

      editor.on('keyup', async e => {
        if ((e.metaKey || e.ctrlKey) && e.keyCode === KeyCode.X) {
          await handleXYZ(editor);
        }

        verifyLinks(editor);
      });

      editor.on('PastePostProcess', e => importPastedLinks(e, editor));

      // Re-sync resource-link visibility after a native undo/redo. This runs
      // after the DOM has been restored, so links brought back (or removed
      // again) by undo/redo are reflected in the Review Links list.
      editor.on('Undo Redo', () => {
        const existingLinks = Array.from(
          editor.getBody().querySelectorAll('a[href][id]')
        );

        setLinks(prev =>
          prev.map(link => ({
            ...link,
            vissible: !!existingLinks.find(e => e?.id === link?.Placeholder)
          }))
        );
      });

      editor.ui.registry.addContextToolbar('firstContextToolbar', {
        predicate: node =>
          node.nodeName === 'A' &&
          !node.hasAttribute('data-preview-href') &&
          !node?.classList.contains(selectedProductLinkClassName) &&
          !node?.classList.contains(QUOTE_SHARE_LINK_CLASS),
        items: 'viewlink',
        position: 'node',
        scope: 'node'
      });

      // The same three actions a resource link offers: View opens the share,
      // Edit renames it and Delete removes it from the email (DEV-785).
      editor.ui.registry.addContextToolbar('quoteShareContextToolbar', {
        predicate: node =>
          node.nodeName === 'A' &&
          node?.classList.contains(QUOTE_SHARE_LINK_CLASS),
        items: 'viewlink editlink deletelink',
        position: 'node',
        scope: 'node'
      });

      editor.ui.registry.addContextToolbar('secondContextToolbar', {
        predicate: node =>
          node.nodeName === 'A' &&
          node?.hasAttribute('id') &&
          node.hasAttribute('data-preview-href'),
        items: 'viewlink editlink deletelink',
        position: 'node',
        scope: 'node'
      });

      editor.ui.registry.addIcon('view', viewIcon);
      editor.ui.registry.addIcon('edit', editIcon);
      editor.ui.registry.addIcon('trash', removeIcon);

      editor.ui.registry.addButton('viewlink', {
        icon: 'view',
        text: 'View',
        tooltip: 'View Link',
        onAction: () =>
          handleLinks(EditorActions.VIEW, {
            editorNode: editor
          })
      });

      editor.ui.registry.addButton('editlink', {
        icon: 'edit',
        text: 'Edit',
        tooltip: 'Edit Link',
        onAction: () =>
          handleLinks(EditorActions.EDIT, {
            editorNode: editor
          })
      });

      editor.ui.registry.addButton('deletelink', {
        icon: 'trash',
        text: 'Delete',
        tooltip: 'Delete Link',
        onAction: () => {
          handleLinks(EditorActions.DELETE, {
            editorNode: editor
          });
          // Refocus the editor so a following Cmd/Ctrl+Z reaches TinyMCE's
          // undo manager and restores the deleted link.
          editor.focus();
        }
      });
    }
  };

  return {
    links: !editorRef
      ? importedLinks
      : links?.filter(link => link?.vissible) || [],
    currentId,
    initLinkOptions,

    setLinks,
    handleLinks,
    setCurrentId,
    handleKeyDown,
    handleSnippet,
    importPastedLinks,
    // The quote share link is edited straight in the body — see the functions
    // themselves (DEV-785).
    renameQuoteShareLink,
    removeQuoteShareLink
  };
};
